import { css, Global } from '@emotion/react';
// SOURCE: https://www.figma.com/design/scIi9Ha3DWX8NYVZdNd5Ww/Kai-Design-System?node-id=48-45&p=f&t=dY28JrD0uHeUDnaz-0

const black = '#000000'; // hsl(0, 0.00%, 0.00%) figma name: black
const offBlack = '#171717'; // hsl(0, 0.00%, 8.80%) figma name: off-black
const charcoal = '#1D1D1D'; // hsl(0, 0.00%, 11.40%) figma name: charcoal
const darkestGray = '#2e2e2e'; // hsl(0, 0.00%, 18.00%) figma name: grey-1
const darkerGray = '#565656'; // hsl(0, 0.00%, 33.70%) figma name: grey-1
const darkGray = '#898989'; // hsl(0, 0.00%, 53.70%) figma name: grey-2
const gray = '#B7B7B7'; // hsl(0, 0.00%, 71.80%) figma name: grey-3
const lighterGray = '#c9c9c9'; // hsl(0, 0.00%, 78.80%) figma name: grey-3.5
const lightGray = '#E0E0E0'; // hsl(0, 0.00%, 87.80%) figma name: grey-4
const lightestGray = '#EDEDED'; // hsl(0, 0.00%, 92.90%) figma name: grey-5
const offWhite = '#F6F6F6'; // hsl(0, 0.00%, 96.50%) figma name: off-white
const white = '#FFFFFF'; // hsl(0, 0.00%, 100.00%) figma name: white

const blackTransparent1 = 'rgba(0, 0, 0, 0.94)'; // black with transparency: 1 - 1/16th
const blackTransparent2 = 'rgba(0, 0, 0, 0.88)'; // black with transparency: 1 - 2/16th
const blackTransparent3 = 'rgba(0, 0, 0, 0.81)'; // black with transparency: 1 - 3/16th
const blackTransparent4 = 'rgba(0, 0, 0, 0.75)'; // black with transparency: 1 - 4/16th
const blackTransparent5 = 'rgba(0, 0, 0, 0.69)'; // black with transparency: 1 - 5/16th
const blackTransparent6 = 'rgba(0, 0, 0, 0.63)'; // black with transparency: 1 - 6/16th
const blackTransparent7 = 'rgba(0, 0, 0, 0.44)'; // black with transparency: 1 - 7/16th

const blackAndWhite0 = '#000000'; // hsl(0, 0.00%, 0.00%) = black
const blackAndWhite1 = '#111111'; // hsl(0, 0.00%, 6.66%)
const blackAndWhite1p5 = '#1a1a1a'; // hsl(0, 0.00%, 10.20%) ~ charcoal (within 3/256 of the figma name)
const blackAndWhite2 = '#222222'; // hsl(0, 0.00%, 13.33%)
const blackAndWhite2p5 = '#2a2a2a'; // hsl(0, 0.00%, 16.66%)
const blackAndWhite3 = '#333333'; // hsl(0, 0.00%, 20.00%)
const blackAndWhite4 = '#444444'; // hsl(0, 0.00%, 26.66%)
const blackAndWhite5 = '#555555'; // hsl(0, 0.00%, 33.33%) ~= darkerGray (within 1/256 of the figma name)
const blackAndWhite6 = '#666666'; // hsl(0, 0.00%, 40.00%)
const blackAndWhite7 = '#777777'; // hsl(0, 0.00%, 46.66%)
const blackAndWhite8 = '#888888'; // hsl(0, 0.00%, 53.33%) ~= darkGray (within 1/256 of the figma name)
const blackAndWhite9 = '#999999'; // hsl(0, 0.00%, 60.00%)
const blackAndWhite10 = '#aaaaaa'; // hsl(0, 0.00%, 66.66%)
const blackAndWhite11 = '#bbbbbb'; // hsl(0, 0.00%, 73.33%) ~ gray (within 4/256 of the figma name)
const blackAndWhite12 = '#cccccc'; // hsl(0, 0.00%, 80.00%)
const blackAndWhite13 = '#dddddd'; // hsl(0, 0.00%, 86.66%) ~ lightGray (within 3/256 of the figma name)
const blackAndWhite14 = '#eeeeee'; // hsl(0, 0.00%, 93.33%)
const blackAndWhite14p5 = '#f7f7f7'; // hsl(0, 0.00%, 96.90%)
const blackAndWhite15 = '#ffffff'; // hsl(0, 0.00%, 99.9%) = white

const veryDarkBlue = '#041F27';
const darkBlue = '#40879F';
const darkishBlue = '#7abcd2';
const blue = '#95D5EB';
const lightBlue = '#c0e9f0';
const offWhiteBlue = '#F4F8F9';

const veryDarkGreen = '#062604';
const darkGreen = '#30803d';
const green = '#7cb88e';
const lightGreen = '#BFDCC8';
const offWhiteGreen = '#F4F9F6';

const darkRed = '#611818';
const red = '#8c1a1a';
const lightRed = '#bf3b3b';
const lighterRed = '#d8a3a3';
const offWhiteRed = '#ffe6e5';

const veryDarkOrange = '#834506';
const darkOrange = '#d1812c';
const orange = '#ffb05d';
const lightOrange = '#f6d0a7';
const offWhiteOrange = '#f8ede1';

const veryDarkYellow = '#4d420e';
const darkYellow = '#9f8402';
const yellow = '#fddb33';
const lightYellow = '#fde99a';
const offWhiteYellow = '#FEFCEE';

const namedColors = {
  offBlack,
  charcoal,
  gray,
  darkGray,
  darkestGray,
  lighterGray,
  lightGray,
  lightestGray,
  darkerGray,
  offWhite,
  black,
  white,

  veryDarkBlue,
  darkBlue,
  darkishBlue,
  blue,
  lightBlue,
  offWhiteBlue,

  veryDarkGreen,
  darkGreen,
  green,
  lightGreen,
  offWhiteGreen,

  darkRed,
  red,
  lightRed,
  lighterRed,
  offWhiteRed,

  veryDarkOrange,
  darkOrange,
  orange,
  lightOrange,
  offWhiteOrange,

  veryDarkYellow,
  darkYellow,
  yellow,
  lightYellow,
  offWhiteYellow,
};

export type NamedColor = keyof typeof namedColors;

const alwaysDark = {
  background: {
    black,
    dark: blackAndWhite1p5,
    light: blackAndWhite2,
    lighter: blackAndWhite13,
  },
  text: {
    black: blackAndWhite0,
    darkGray: blackAndWhite8,
    lightGray: blackAndWhite12,
    lighterGray: blackAndWhite14,
    green,
    white,
  },
  border: {
    darkGray: blackAndWhite3,
  },
  primary: yellow,

  black,
  charcoal,

  darkestGray,
  darkerGray,
  darkGray,
  gray,
  lightGray,
  lightestGray,
  offWhite,
  white,

  darkBlue,
  darkishBlue,
  blue,
  lightBlue,
  offWhiteBlue,

  darkGreen,
  green,
  lightGreen,
  greenLight: lightGreen,
  offWhiteGreen,

  darkRed,
  red,
  lightRed,
  lighterRed,
  offWhiteRed,

  veryDarkOrange,
  darkOrange,
  orange,
  lightOrange,
  offWhiteOrange,

  darkYellow,
  yellow,
  lightYellow,
  offWhiteYellow,

  blackTransparent1,
  blackTransparent2,
  blackTransparent3,
  blackTransparent4,
  blackTransparent5,
  blackTransparent6,
  blackTransparent7,
};

/**
 * Color numbering, e.g. background(1), background2, background3, etc. goes from highest contrast to lowest contrast.
 * So, for the highest contrast between text and background: use --background and --text
 * For the lowest contrast: use --background4 and --text2
 *
 * For all colors: background and background1 are the same (with or without the "1")
 *
 * For the Light more: backgrounds start off close to white and then go to darker colors. Text starts black and then goes to lighter colors.
 * For the Dark more: backgrounds start off close to black and then go to lighter colors. Text starts white and then goes to darker colors.
 *
 * Match inverseBackground with inverseText; numbering will still apply - lower numbers == higher contrast
 *
 * Naming scheme note: foo and foo1 are always the same color and should be the default option. foo0 is an "extreme contrast" option to be used sparingly.
 * Increasing values signify decreasing contrast.
 */
const light = {
  leftNavSidebar: blackAndWhite2,
  background: blackAndWhite15, // use in css: var(--background)
  background1: blackAndWhite15, // use in css: var(--background)
  background1p5: blackAndWhite14p5, // use in css: var(--background1p5)
  background2: blackAndWhite14, // use in css: var(--background2)
  background3: blackAndWhite13, // use in css: var(--background3)
  background4: blackAndWhite12, // use in css: var(--background4)

  border0: blackAndWhite7,
  border: blackAndWhite9, // use in css: var(--border)
  border1: blackAndWhite9, // use in css: var(--border1)
  border2: blackAndWhite11, // use in css: var(--border2)
  border3: blackAndWhite13, // use in css: var(--border3)

  // alt backgrounds are the same for light mode, but in dark mode, the ramp the opposite direction
  altBackgroundForDarkMode: blackAndWhite15, // use in css: var(--background)
  altBackgroundForDarkMode1: blackAndWhite15, // use in css: var(--background)
  altBackgroundForDarkMode2: blackAndWhite14, // use in css: var(--background2)
  altBackgroundForDarkMode3: blackAndWhite13, // use in css: var(--background3)
  altBackgroundForDarkMode4: blackAndWhite12, // use in css: var(--background4altB

  // alt borders are the same for light mode, but in dark mode, the ramp the opposite direction
  altBorderForDarkMode0: blackAndWhite5,
  altBorderForDarkMode: blackAndWhite6, // use in css: var(--border)
  altBorderForDarkMode1: blackAndWhite6, // use in css: var(--border1)
  altBorderForDarkMode2: blackAndWhite7, // use in css: var(--border2)
  altBorderForDarkMode3: blackAndWhite8, // use in css: var(--border3)

  text0: blackAndWhite0,
  text: blackAndWhite1, // use in css: var(--text)
  text1: blackAndWhite1, // use in css: var(--text1)
  text2: blackAndWhite4, // use in css: var(--text2)
  text3: blackAndWhite7, // use in css: var(--text3)
  text4: blackAndWhite10, // use in css: var(--text4)
  text5: blackAndWhite13, // use in css: var(--text5)

  backgroundBlue: offWhiteBlue, // use in css: var(--backgroundBlue)
  backgroundBlue1: offWhiteBlue,
  backgroundBlue2: lightBlue,
  backgroundBlue3: blue,

  hoverBackground: blue,
  selectedBackground: blue,

  backgroundYellow: offWhiteYellow, // use in css: var(--backgroundYellow)
  backgroundYellow1: offWhiteYellow, // use in css: var(--backgroundYellow1)
  backgroundYellow2: lightYellow, // use in css: var(--backgroundYellow2)
  backgroundYellow3: yellow, // use in css: var(--backgroundYellow3)

  backgroundGreen: offWhiteGreen, // use in css: var(--backgroundGreen)
  backgroundGreen1: offWhiteGreen, // use in css: var(--backgroundGreen1)
  backgroundGreen2: lightGreen, // use in css: var(--backgroundGreen2)
  backgroundGreen3: green, // use in css: var(--backgroundGreen3)

  textGreen: darkGreen, // use in css: var(--textGreen)
  textGreen1: darkGreen, // use in css: var(--textGreen1)
  textGreen2: green, // use in css: var(--textGreen2)
  textGreen3: lightGreen, // use in css: var(--textGreen3)

  textBlue: darkBlue,

  red,
  // blue: darkBlue,

  // DEPRECATED - use alwaysDark instead
  // inverseText: white,                  // use in css: var(--inverseText)
  // inverseBackground: darkestGray,      // use in css: var(--inverseBackground)
  // buttonActiveBackground: green, // use in css: var(--buttonActiveBackground)
  // buttonActiveText: offWhite,          // use in css: var(--buttonActiveText)
  // buttonDisabledBackground: gray,      // use in css: var(--buttonDisabledBackground)
  // buttonDisabledText: offWhite,        // use in css: var(--buttonDisabledText)
};

export type CssColorVars = typeof light;

const dark: Record<keyof typeof light, string> = {
  leftNavSidebar: blackAndWhite2,

  background: blackAndWhite2,
  background1: blackAndWhite2,
  background1p5: blackAndWhite2p5,
  background2: blackAndWhite3,
  background3: blackAndWhite4,
  background4: blackAndWhite5,

  border0: blackAndWhite7,
  border: blackAndWhite6,
  border1: blackAndWhite6,
  border2: blackAndWhite5,
  border3: blackAndWhite4,

  altBackgroundForDarkMode: blackAndWhite4,
  altBackgroundForDarkMode1: blackAndWhite4,
  altBackgroundForDarkMode2: blackAndWhite3,
  altBackgroundForDarkMode3: blackAndWhite2,
  altBackgroundForDarkMode4: blackAndWhite1,

  altBorderForDarkMode0: blackAndWhite1,
  altBorderForDarkMode: blackAndWhite2,
  altBorderForDarkMode1: blackAndWhite2,
  altBorderForDarkMode2: blackAndWhite3,
  altBorderForDarkMode3: blackAndWhite4,

  text0: blackAndWhite13,
  text: blackAndWhite11,
  text1: blackAndWhite11,
  text2: blackAndWhite10,
  text3: blackAndWhite9,
  text4: blackAndWhite8,
  text5: blackAndWhite7,

  backgroundBlue: veryDarkBlue,
  backgroundBlue1: veryDarkBlue,
  backgroundBlue2: darkBlue,
  backgroundBlue3: darkBlue,

  hoverBackground: darkBlue,
  selectedBackground: darkBlue,

  backgroundYellow: veryDarkYellow,
  backgroundYellow1: veryDarkYellow,
  backgroundYellow2: darkYellow,
  backgroundYellow3: yellow,

  backgroundGreen: veryDarkGreen,
  backgroundGreen1: veryDarkGreen,
  backgroundGreen2: darkGreen,
  backgroundGreen3: green,

  textGreen: lightGreen,
  textGreen1: lightGreen,
  textGreen2: green,
  textGreen3: green,

  textBlue: lightBlue,

  red,
  // blue: darkBlue,

  // DEPRECATED - use alwaysDark instead
  // inverseBackground: lightGray,
  // inverseText: darkestGray,
  // buttonActiveBackground: green,
  // buttonActiveText: black,
  // buttonDisabledBackground: gray,
  // buttonDisabledText: black,
};

const vars = (theme: Record<string, string>) => Object.entries(theme).map(([k, v]) => `--${k}:${v};`).join('');

export const darkLightModeSupportEnabled = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).has('auto') : false;
export const forceDarkMode = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).has('dark') : false;

export const darkLightModeCss = css`
  :root { ${vars(light)} }
  @media (prefers-color-scheme: dark) { :root { ${vars(dark)} } }
  :root[data-theme='light'] { ${vars(light)} }
  :root[data-theme='dark'] { ${vars(dark)} }

  @media (prefers-color-scheme: light) {
    [style*="--light-color"],
    [style*="--light-background-color"],
    [style*="--light-border-color"] {
      color: var(--light-color, var(--dark-color));
      background-color: var(--light-background-color, var(--dark-background-color));
      border-color: var(--light-border-color, var(--dark-border-color));
    }
  }

  @media (prefers-color-scheme: dark) {
    [style*="--dark-color"],
    [style*="--dark-background-color"],
    [style*="--dark-border-color"] {
      color: var(--dark-color, var(--light-color));
      background-color: var(--dark-background-color, var(--light-background-color));
      border-color: var(--dark-border-color, var(--light-border-color));
    }
  }
`;

export const justDarkModeCss = css`
  :root { ${vars(dark)} }

  [style*="--light-color"],
  [style*="--light-background-color"],
  [style*="--light-border-color"],
  [style*="--dark-color"],
  [style*="--dark-background-color"],
  [style*="--dark-border-color"] {
    color: var(--dark-color, var(--light-color));
    background-color: var(--dark-background-color, var(--light-background-color));
    border-color: var(--dark-border-color, var(--light-border-color));
  }
`;

export const justLightModeCss = css`
  :root { ${vars(light)} }

  [style*="--light-color"],
  [style*="--light-background-color"],
  [style*="--light-border-color"],
  [style*="--dark-color"],
  [style*="--dark-background-color"],
  [style*="--dark-border-color"] {
    color: var(--light-color, var(--dark-color));
    background-color: var(--light-background-color, var(--dark-background-color));
    border-color: var(--light-border-color, var(--dark-border-color));
  }
`;

export const GlobalColorVarsCss = () => (
  <Global
    styles={darkLightModeSupportEnabled
      ? darkLightModeCss
      : forceDarkMode
      ? justDarkModeCss
      : justLightModeCss}
  />
);

const statusColors = {
  notStarted: {
    background: lightGray,
    color: charcoal,
  },
  inProgress: {
    background: red,
    color: white,
  },
  completed: {
    background: lightGray,
    color: charcoal,
  },
  created: {
    background: lightGray,
    color: charcoal,
  },
  dispatched: {
    background: lightGray,
    color: charcoal,
  },
  pendingReview: {
    background: darkBlue,
    color: white,
  },
  inReview: {
    background: blue,
    color: white,
  },
  approved: {
    background: green,
    color: white,
  },
  rejected: {
    background: yellow,
    color: black,
  },
  correctionsRequested: {
    background: yellow,
    color: black,
  },
  archived: {
    background: gray,
    color: charcoal,
  },
};
export const COLORS2 = { alwaysDark, light, dark, statusColors, namedColors };
